import { Card, Grid, LinearProgress, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateComponent, DateVariant } from "components/base/DateComponent";
import { Section } from "components/disw";
import ErrorBox from "components/error/ErrorBox";
import React from "react";
import { useHistory } from "react-router-dom";
import { createRoute } from "utils/url";
import { useRateAgreementById } from "./hooks/useRateAgreementById";
import RateTableSummary from "./RateTableSummary";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2)
  },
  card: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: "#ffffff",
    border: "1px solid #00000026",
    borderRadius: 3,
    boxShadow: "0px 1px 2px 0px #0000004D"
  },
  propertyGrid: {
    minWidth: 112,
    maxWidth: 112,
    width: 112,
    flexBasis: 112,
    flexShrink: 0,
    flexGrow: 0,
    paddingRight: theme.spacing(1)
  },
  propertyRow: {
    marginBottom: theme.spacing(0.5)
  },
  property: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#1E1E1E"
  },
  value: {
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    color: "#464646"
  },
  agreementGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    columnGap: theme.spacing(3),
    rowGap: theme.spacing(2)
  },
  columnDivider: {
    // intentionally empty — layout handled by grid gap
  },
  rateTableCard: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: "#ffffff",
    border: "1px solid #00000026",
    borderRadius: 3,
    boxShadow: "0px 1px 2px 0px #0000004D",
    cursor: "pointer",
    "&:hover": { backgroundColor: "#EEF6F8" }
  },
  rateTableName: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    color: "#1E1E1E",
    marginBottom: theme.spacing(1)
  }
}));

interface PropertyRowProps {
  label: string;
  children: React.ReactNode;
  testId?: string;
}

function PropertyRow({ label, children, testId }: PropertyRowProps) {
  const classes = useStyles();
  return (
    <Grid
      container
      wrap="nowrap"
      alignItems="flex-start"
      className={classes.propertyRow}
    >
      <Grid item className={classes.propertyGrid}>
        <Typography className={classes.property}>{label}</Typography>
      </Grid>
      <Grid item xs>
        <Typography
          className={classes.value}
          component="div"
          data-testid={testId}
        >
          {children}
        </Typography>
      </Grid>
    </Grid>
  );
}

interface RateAgreementDetailsProps {
  agreementId: string;
}

export default function RateAgreementDetails({
  agreementId
}: RateAgreementDetailsProps) {
  const classes = useStyles();
  const history = useHistory();
  const { rateAgreement, loading, error } = useRateAgreementById(agreementId);

  if (loading) return <LinearProgress />;
  if (error) return <ErrorBox apolloError={error} />;
  if (!rateAgreement) return null;

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <Section name="Agreement Details">
          <div className={classes.agreementGrid}>
            {/* Col 1 — Identity */}
            <div>
              <PropertyRow label="Name" testId="detail-name">
                {rateAgreement.name}
              </PropertyRow>
              <PropertyRow label="Description" testId="detail-description">
                {rateAgreement.description ?? "\u2014"}
              </PropertyRow>
            </div>

            {/* Col 2 — Contract scope */}
            <div className={classes.columnDivider}>
              <PropertyRow label="Carrier" testId="detail-carrier">
                {rateAgreement.carrier}
              </PropertyRow>
              <PropertyRow
                label="Service Method"
                testId="detail-service-method"
              >
                {rateAgreement.serviceMethod}
              </PropertyRow>
            </div>

            {/* Col 3 — Audit */}
            <div className={classes.columnDivider}>
              <PropertyRow
                label="Last Updated By"
                testId="detail-last-modified-by"
              >
                {rateAgreement.lastModifiedBy}
              </PropertyRow>
              <PropertyRow
                label="Last Updated At"
                testId="detail-last-modified-at"
              >
                <DateComponent
                  date={rateAgreement.lastModifiedAt.utcDateTime}
                  variant={DateVariant.SHORT_WITH_TIME}
                />
              </PropertyRow>
            </div>
          </div>
        </Section>

        <Section name="Rate Tables">
          {rateAgreement.rateTables.map(rt => (
            <div
              key={rt.id}
              className={classes.rateTableCard}
              data-testid={`rate-table-card-${rt.id}`}
              role="button"
              tabIndex={0}
              onClick={() =>
                history.push(
                  createRoute(`/rate-agreements/${agreementId}/${rt.id}`)
                )
              }
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  history.push(
                    createRoute(`/rate-agreements/${agreementId}/${rt.id}`)
                  );
                }
              }}
            >
              <Typography
                className={classes.rateTableName}
                data-testid={`rate-table-card-name-${rt.id}`}
              >
                {rt.name}
                {rt.description ? ` \u2014 ${rt.description}` : ""}
              </Typography>
              <RateTableSummary rateTable={rt} />
            </div>
          ))}
        </Section>
      </Card>
    </div>
  );
}
